// ============================================================
// contrast.js — Utilidades WCAG: colores, luminancia, safe()
// Extraído de script.js (líneas 27-104)
// ============================================================

// CICLO CONSCIENTE con `foto-fondo.js`, y es seguro por construcción: aquel importa de éste
// (hexToRgb/rgbToHex/wcagRatio/checkAndFixContrast/TARGET_TEXT) y éste de aquél, pero NINGUNO
// de los dos usa un binding del otro al EVALUAR el módulo — solo dentro de cuerpos de función.
// Con ESM eso resuelve igual en los dos órdenes de carga (esbuild y node), y lo ejercita
// `tests/test_foto_fondo.py::ModuloPuro`, que importa cada uno por su cuenta.
// La alternativa —duplicar aquí la regla de la foto— la desincronizaría del editor y del
// arnés de paridad con Python, que es justo el fallo que este PDR arregla.
import { pintaFotoDeFondo, textoSobreFoto } from './foto-fondo.js';
// La marca «a mano» (utils/manual.js, módulo BASE sin imports: no cierra ningún ciclo).
import { esManual } from './manual.js';

// ── TARGETS DE CONTRASTE — SSOT del builder (invariante nº19) ────────────────
// Antes vivían como números sueltos: safe() usaba 7 y 3.0, pero palettes.js
// (applyPaletteToBlocks) corregía el acento a 4.5 y lo HORNEABA en props → el
// re-paletizado reintroducía por la puerta de atrás la regresión de "botones
// negros" que el 3.0 de safe() existe para evitar, y encima persistida (llega
// al sitio publicado). Un único par de constantes, importado por todos:
//
//   TARGET_TEXT (7)   — TEXTO de párrafo/titular: contraste MARCADO, por encima
//                       del 4.5 AA pelado. Legibilidad primero.
//   TARGET_ACCENT (3) — ACENTO: es un FILL de componente UI (WCAG 1.4.11 pide
//                       3:1, no 4.5:1). A 4.5 un accent vibrante medio (#e67e22,
//                       ratio ~3.05 vs blanco) se oscurecía a naranja quemado
//                       casi negro sobre secciones claras. 3.0 mueve la
//                       luminosidad lo justo sin matar el tono de marca.
//
// El ESPEJO en Python vive en core_services/color_utils.py (TARGET_TEXT /
// TARGET_ACCENT). Si cambias uno, cambia el otro: `checkAndFixContrast` y
// `color_utils.fix_contrast` son port 1:1 y comparten estos targets.
export const TARGET_TEXT = 7;
export const TARGET_ACCENT = 3;

/**
 * ALLOWLIST DE UN VALOR CSS LEGÍTIMO — la MISMA regla para los tokens de sitio y para los
 * props de color de cada bloque. Vivía en `core/render.js`, pero ese módulo importa de aquí
 * (`secondaryOf`) y arrastra `store.js` + DOM: si `contrast.js` importara de él se cerraría un
 * ciclo y todos los bloques dejarían de ser cargables fuera del navegador. Así que la
 * definición baja al módulo BASE (que no importa nada) y `render.js` la RE-EXPORTA: sigue
 * habiendo UNA sola copia y `viewer.js` la importa exactamente donde la importaba.
 *
 * Qué deja fuera y por qué: `"` rompería un atributo `style`, `;`/`}`/`<` romperían el bloque
 * `<style>` del head. Pasan los formatos reales del builder: `#rrggbb`, `rgb()/rgba()`,
 * `color-mix(in srgb, …)`, `var(--x)`, `linear-gradient(...)` y los nombres CSS.
 *
 * Dos precisiones que costaron un hallazgo cada una:
 *   · `url(…)` NO pasa. La lista de caracteres sola lo dejaba entrar (`url(//host/x.png)` no
 *     necesita `:`), y estos valores no siempre caen en `background-color`: `ai-custom.js` y
 *     el bloque roto de `render.js` los interpolan en `background:`, donde `url()` es válido y
 *     el navegador HACE la petición → cada visitante filtraría su IP a un tercero.
 *   · las letras NO ASCII SÍ pasan (`\p{L}`, no `\w`, que en JS es solo `[A-Za-z0-9_]`).
 *     En Python `\w` sobre `str` es Unicode, así que con `\w` a secas un `fontFamily:
 *     'Übersicht'` se GUARDABA en el servidor y lo borraba este filtro en cada render: la
 *     fuente caía a Inter sin que nadie supiera por qué. Una letra acentuada no rompe nada.
 * Espejo en Python: `views.py::_css_token_ok` (mismo criterio, en la ESCRITURA); la paridad
 * la guarda un test que ejercita AMBAS con el mismo corpus, no comparando el texto del patrón.
 */
const _CSS_CHARS_OK = /^[\p{L}\p{N}_\s(),.%#+/-]*$/u;
const _CSS_PIDE_RED = /url\s*\(|\/\//;
export const cssTokenSafe = v => {
    const s = String(v);
    return _CSS_CHARS_OK.test(s) && !_CSS_PIDE_RED.test(s);
};

/**
 * Props que TODOS los renderers interpolan CRUDOS dentro del atributo `style` del `<section>`
 * (`style="--bg:${p.bgColor}; --text:${p.textColor}; …"`, 45 puntos en `js/blocks/*.js`).
 * Como salen de `datos_json_builder`, un valor con `"` cierra el atributo e inyecta los que
 * quiera (`onmouseover=…`) en el EDITOR y en la vía SPA pública, que NO pasan por el saneador
 * server-side. Filtrarlos en `safe()` es el CINTURÓN: cubre también los sitios cuyo JSON ya
 * tiene un valor hostil guardado, sin esperar a que su dueño lo vuelva a guardar.
 * La frontera de ESCRITURA es `views.py::_sanitizar_props_css`.
 */
export const PROPS_CSS_INTERPOLADOS = ['bgColor', 'textColor', 'accentColor', 'accentContrast', 'fontFamily'];

export function hexToRgb(color) {
    if (!color || typeof color !== 'string') return [0,0,0];
    
    if (color.startsWith('rgb')) {
        let m = color.match(/\d+/g);
        if (m && m.length >= 3) return [parseInt(m[0]), parseInt(m[1]), parseInt(m[2])];
    }
    
    let h = color.replace('#', '').trim();
    if(h.length === 3) h = [...h].map(x => x+x).join('');
    if(h.length >= 6) h = h.slice(0, 6);
    if(h.length !== 6) return [0,0,0]; 
    
    let r = parseInt(h.slice(0, 2), 16);
    let g = parseInt(h.slice(2, 4), 16);
    let b = parseInt(h.slice(4, 6), 16);
    
    if (isNaN(r) || isNaN(g) || isNaN(b)) return [0,0,0]; 
    return [r, g, b];
}

export function getLuminance(r, g, b) {
    let a = [r, g, b].map(function (v) {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

/**
 * Ratio de contraste WCAG entre dos colores (1..21). ESPEJO de `color_utils.wcag_ratio`.
 *
 * Existía inline dentro de `checkAndFixContrast` y en ningún sitio más, así que quien quería
 * COMPROBAR un contraste (en vez de corregirlo) tenía que recalcularlo a mano. Lo necesita
 * `utils/foto-fondo.js` para su guarda de "nunca empeorar": `checkAndFixContrast` NO es
 * monótono —con un objetivo inalcanzable devuelve su respaldo nuclear, que puede medir MENOS
 * que el color que se le pasó— y la única forma de detectarlo es medir las dos opciones.
 */
export function wcagRatio(hexA, hexB) {
    const la = getLuminance(...hexToRgb(hexA));
    const lb = getLuminance(...hexToRgb(hexB));
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function checkAndFixContrast(bgHex, textHex, target = 4.5) {
    const bgRgb = hexToRgb(bgHex);
    const textRgb = hexToRgb(textHex);
    const lumBg = getLuminance(...bgRgb);
    const lumText = getLuminance(...textRgb);

    // --- Verificar ratio (target configurable: 4.5 mínimo AA, 7 = más marcado) ---
    const ratio = (Math.max(lumBg, lumText) + 0.05) / (Math.min(lumBg, lumText) + 0.05);
    if (ratio >= target) return textHex;

    // --- Ajuste HSL: solo mover Lightness, preservar Hue + Saturation ---
    // Resultado: texto azul roto -> azul oscuro legible, NO negro. Mantiene el tono.
    const [h, s] = rgbToHsl(...textRgb);
    const isBgLight = lumBg > 0.179;

    let newL = isBgLight ? 0.2 : 0.8;
    const step = isBgLight ? -0.05 : 0.05;

    for (let i = 0; i < 15; i++) {
        newL = Math.max(0, Math.min(1, newL + step));
        const [rr, gg, bb] = hslToRgb(h, s, newL);
        const newLum = getLuminance(rr, gg, bb);
        const newRatio = (Math.max(lumBg, newLum) + 0.05) / (Math.min(lumBg, newLum) + 0.05);
        if (newRatio >= target) return rgbToHex(rr, gg, bb);
    }

    // --- Fallback nuclear: casi nunca llega aquí (saturación extrema imposible de ajustar) ---
    return isBgLight ? '#111827' : '#f1f5f9';
}

/**
 * @param {Object} rawProps - props CRUDAS del bloque.
 * @param {string} [type]   - TIPO del bloque. Segundo parámetro NUEVO y OPCIONAL: solo lo
 *   necesita la regla de FOTO DE FONDO (hero v1/v2), que no se puede decidir mirando los props.
 *   Sin él, `safe()` se comporta exactamente como antes — y ninguna de las 31 llamadas de
 *   `js/blocks/*.js` pasaba un 2º argumento, así que el único call site que lo estrena es
 *   `blocks/hero.js`.
 */
export function safe(rawProps, type) {
    let p = { ...rawProps };

    // Normalizar snake_case → camelCase (Gemini a veces los devuelve así)
    const SNAKE_MAP = {
        bg_color: 'bgColor', text_color: 'textColor', accent_color: 'accentColor',
        secondary_color: 'secondaryColor',
        card_bg: 'cardBg', font_family: 'fontFamily', accent_contrast: 'accentContrast',
        text_contrast: 'textContrast', map_embed: 'map_embed' // map_embed se mantiene
    };
    Object.entries(SNAKE_MAP).forEach(([snake, camel]) => {
        if (p[snake] !== undefined && p[camel] === undefined) {
            p[camel] = p[snake];
            delete p[snake];
        }
    });

    // CINTURÓN XSS (ver PROPS_CSS_INTERPOLADOS): un valor que no es un valor CSS se BORRA y el
    // bloque cae a su default de abajo (#ffffff / #1a1a1a / #6366f1 / 'Inter'), así que se sigue
    // pintando. Va DESPUÉS de normalizar snake_case (para filtrar también los `bg_color`) y
    // ANTES de los defaults y del cálculo de contraste, que leen ya los valores limpios.
    PROPS_CSS_INTERPOLADOS.forEach(k => {
        if (p[k] != null && !cssTokenSafe(p[k])) delete p[k];
    });
    
    // Textos de copy que los renderers interpolan a pelo (`${p.title}` en hero, menu, services,
    // process, bento, journey, trust; `${p.cta}` en hero y content). Si el modelo OMITE la clave
    // —pasó en un clonado (2026-09-09): hero sin title/subtitle/cta— el template literal pinta la
    // palabra "undefined", el cliente la ve y, si guarda, el extractor la hornea como texto. '' es
    // lo que el extractor ya devuelve para un campo vacío y ningún bloque distingue '' de undefined.
    // `logo` va en la lista por el mismo motivo y con una vuelta de tuerca: `header.js` lo
    // coalescía por su cuenta (`{ logo: rawProps.logo || '', ...safe(rawProps) }`), pero el
    // spread va DESPUÉS y `safe()` copia `{...rawProps}`, así que un `"logo": null` explícito
    // (clonado, o JSON del modelo) pisaba el apaño y se publicaba la palabra «null» como marca
    // en TODAS las páginas; al primer guardado el extractor la horneaba. Aquí no hay orden que
    // valga: se normaliza en el embudo por el que pasan los 34 bloques.
    ['title', 'subtitle', 'cta', 'logo'].forEach(k => { if (p[k] == null) p[k] = ''; });

    p.bgColor = p.bgColor || "#ffffff";
    p.textColor = p.textColor || "#1a1a1a";
    p.accentColor = p.accentColor || "#6366f1";
    // textColor: contraste MARCADO (TARGET_TEXT=7, no el 4.5 pelado) → texto neutro siempre
    // claramente legible. Preserva tono si el texto es de color.
    // FOTO DE FONDO (hero v1/v2): ahí `bgColor` NO es el fondo —solo colorea el velo— y
    // contrastar contra él dejaba el titular oscuro sobre una foto oscura en CADA render,
    // deshaciendo lo que el saneador del servidor había medido bien. `textoSobreFoto` mide
    // contra el velo compuesto sobre la foto y trae su propia guarda de "nunca empeorar"
    // (`checkAndFixContrast` no es monótono). Ver utils/foto-fondo.js.
    // A MANO (fila «Personalizado» de Color de la sección, `utils/manual.js`): NO se corrige. Lo
    // pintado es lo elegido (D1 del dueño, 16-10-2026: «se respeta lo elegido»); el aviso de
    // contraste y el «Arreglar» los da el panel. Sin esto el color elegido «no se veía»: cada render
    // lo movía a 7:1. El fondo nunca se corrige, así que con el fondo a mano no hay nada que saltar.
    if (!esManual(p, 'textColor')) {
        p.textColor = pintaFotoDeFondo(p, type)
            ? textoSobreFoto(p, type)
            : checkAndFixContrast(p.bgColor, p.textColor, TARGET_TEXT);
    }
    // accentColor: SÍ se corrige, pero a TARGET_ACCENT (3.0, no 4.5) — el porqué está en la
    // cabecera del módulo. El algoritmo (checkAndFixContrast) NO cambia, solo el número.
    p.accentColor = checkAndFixContrast(p.bgColor, p.accentColor, TARGET_ACCENT);
    // secondaryColor (duotono de marca): mismo trato que el acento — es decoración de UI, no
    // texto de párrafo. Solo se toca si el bloque LO DEFINE: no se inventa un secundario, para
    // que un sitio sin él siga pintándose exactamente igual (el CSS cae a var(--secondary,
    // var(--accent))). Ver secondaryOf(), que es quien lo normaliza y valida.
    const _sec = secondaryOf(p);
    if (_sec) p.secondaryColor = _sec;

    let lumAccent = getLuminance(...hexToRgb(p.accentColor));
    p.accentContrast = lumAccent > 0.179 ? "#111827" : "#ffffff";
    
    let lumText = getLuminance(...hexToRgb(p.textColor));
    p.textContrast = lumText > 0.179 ? "#111827" : "#ffffff";

    // Arrays de contenido: se normalizan a `[]` para que ningún render tenga que comprobar el
    // tipo antes de recorrerlos. OJO con la consecuencia, que costó un bug (2026-09-14, un `faq`
    // publicado con su titular sobre un contenedor vacío): `[]` es TRUTHY, así que el
    // `p.items || [...]` de features/services/process/bento NUNCA cae a su default — con el array
    // ausente el bloque se pinta SIN NI UN ITEM, y como los "+" se emiten por item tampoco queda
    // forma de rellenarlo desde el lienzo. Quien lo evita hoy es la guarda de CPU
    // `llm_sanitizers._fix_bloques_sin_contenido` (tabla `_ARRAY_DE_CONTENIDO`), que descarta el
    // bloque al generar en vez de inventarle contenido al cliente.
    p.items = Array.isArray(p.items) ? p.items : [];
    p.plans = Array.isArray(p.plans) ? p.plans : [];
    p.list = Array.isArray(p.list) ? p.list : [];
    let rawLinks = Array.isArray(p.links) ? p.links : [{text: "Inicio", url: "#"}];
    p.links = rawLinks.map(l => typeof l === 'string' ? {text: l, url: "#"} : l);
    
    return p;
}

/**
 * DUOTONO DE MARCA — color SECUNDARIO efectivo de un bloque, o '' si no tiene.
 *
 * El pipeline calcula, protege y persiste `secondaryColor` (llm_prompts lo describe como el
 * color de badges/tags/hover, _fix_brand_palette lo ancla al matiz de marca y el paso "Paleta"
 * lo previsualiza)… y hasta ahora NINGÚN bloque lo pintaba: el segundo color de la marca del
 * cliente moría en el JSON. Esta función es el EMBUDO único que lo saca a la luz:
 *   - safe()          → lo deja normalizado en `p.secondaryColor` para los renders.
 *   - render.js       → lo inyecta como `--secondary` en el style inline del <section>.
 *   - compileSiteHtml → replica la inyección (gotcha nº12) o no llegaría al publicado.
 * El CSS lo consume SIEMPRE como `var(--secondary, var(--accent))`, así que un sitio sin
 * secundario se pinta exactamente igual que antes (no se inventa ningún color).
 *
 * VALIDACIÓN ESTRICTA A HEX (no es cosmética): en compileSiteHtml este valor se interpola como
 * MARKUP dentro del atributo `style` del <section> publicado, y los props salen de
 * datos_json_builder, que las APIs de escritura guardan SIN validar valores. Un `"` rompería el
 * atributo e inyectaría markup. Con la regex, lo que no es un hex literal simplemente no se
 * emite (mismo criterio que la allowlist de site_tokens en render.js/viewer.js).
 *
 * @param {Object} rawProps - props CRUDAS del bloque (acepta snake_case).
 * @returns {string} hex ya corregido en contraste contra el fondo, o '' si no aplica.
 */
export function secondaryOf(rawProps) {
    if (!rawProps || typeof rawProps !== 'object') return '';
    const raw = rawProps.secondaryColor || rawProps.secondary_color;
    if (typeof raw !== 'string') return '';
    const hex = raw.trim();
    if (!/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return '';
    const bgRaw = rawProps.bgColor || rawProps.bg_color;
    const bg = (typeof bgRaw === 'string' && bgRaw.trim()) ? bgRaw.trim() : '#ffffff';
    // Mismo target que el acento: el secundario es decoración de UI (badges, filos, puntos),
    // no texto de párrafo. TARGET_ACCENT evita que un secundario vibrante se apague a casi-negro.
    return checkAndFixContrast(bg, hex, TARGET_ACCENT);
}

export function rgbToHex(r, g, b) {
    const toHex = (c) => { let h = Math.round(c).toString(16); return h.length === 1 ? '0' + h : h; };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // gris acromático
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return [h, s, l]; // valores en [0,1]
}

export function hslToRgb(h, s, l) {
    let r, g, b;
    if (s === 0) {
        r = g = b = l; // gris acromático
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Detecta si un elemento tiene fondo oscuro vía luminancia YIQ.
 * Sube por el DOM hasta encontrar un fondo sólido (no transparente).
 * Centraliza la lógica que estaba duplicada en:
 *   - tools.js (2 closures inline)
 *   - backgrounds.js (calculateIsDark — función local)
 *
 * @param {HTMLElement} el - Elemento de partida (normalmente la <section>)
 * @returns {boolean} true si el fondo es oscuro (YIQ < 128), true como fallback
 */
export function isDarkBackground(el) {
    let current = el;
    while (current && current !== document) {
        const bg = window.getComputedStyle(current).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
            const m = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
            if (m) {
                const yiq = ((parseInt(m[1], 10) * 299) +
                             (parseInt(m[2], 10) * 587) +
                             (parseInt(m[3], 10) * 114)) / 1000;
                return yiq < 128;
            }
        }
        current = current.parentElement;
    }
    return true; // Fallback: el template del builder es oscuro por defecto
}
